import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, Time, LineData, LineSeries, ISeriesApi } from 'lightweight-charts';
import { TechnicalIndicatorData } from '../../api/metrics';
import { TimezoneMode, formatCrosshairLabel, formatTickMarkLabel } from '../../utils/timezone';
import { getDefaultVisibleCandles } from '../../utils/chartZoom';

interface AdxPanelProps {
  data: TechnicalIndicatorData[];
  loading?: boolean;
  timezone?: TimezoneMode;
  granularityMins?: number;
}

/**
 * ADX (Average Directional Index) Panel Component
 * Displays trend strength indicator (0-100 scale)
 * Synchronized with master chart for zoom/pan
 */
const AdxPanel: React.FC<AdxPanelProps> = ({ data, loading, timezone = 'UTC', granularityMins = 60 }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const adxSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);

  useEffect(() => {
    if (!chartContainerRef.current || loading || !data || data.length === 0) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: 'rgba(148, 163, 184, 0.1)' },
        horzLines: { color: 'rgba(148, 163, 184, 0.2)' },
      },
      crosshair: {
        mode: 0,
        vertLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
        horzLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#475569',
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          formatTickMarkLabel(time as number, tickMarkType, timezone),
      },
      localization: {
        timeFormatter: (t: number) => formatCrosshairLabel(t, timezone),
      },
      rightPriceScale: {
        borderColor: '#475569',
        autoScale: false, // Fixed scale for ADX
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    chartRef.current = chart;

    // Configure price scale with fixed Y-axis range for ADX (0-100)
    chart.priceScale('right').applyOptions({
      scaleMargins: {
        top: 0.1,
        bottom: 0.1,
      },
      autoScale: true,
    });

    // Add ADX line series
    const adxSeries = chart.addSeries(LineSeries, {
      color: '#2196F3',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      priceFormat: {
        type: 'price',
        precision: 2,
        minMove: 0.01,
      },
      autoscaleInfoProvider: () => ({
        priceRange: {
          minValue: 0,
          maxValue: 100,
        },
      }),
    });
    adxSeriesRef.current = adxSeries;

    // Convert ADX data to chart format
    const adxData: LineData<Time>[] = data
      .filter(point => point.metricType === 'ADX' && point.period === 14 && point.complete)
      .map(point => ({
        time: (new Date(point.metricTime).getTime() / 1000) as Time,
        value: Number(point.value),
      }))
      .sort((a, b) => (a.time as number) - (b.time as number));

    adxSeries.setData(adxData);

    // Set visible range based on granularity to match main chart
    const defaultVisibleCandles = getDefaultVisibleCandles(granularityMins);
    const totalCandles = adxData.length;

    if (totalCandles > 0) {
        const from = Math.max(0, totalCandles - defaultVisibleCandles);
        const to = totalCandles - 1;
        chart.timeScale().setVisibleLogicalRange({ from, to });
      }

    // Add reference lines (20, 25)
    // Weak trend line (20)
    adxSeries.createPriceLine({
      price: 20,
      color: '#64748b',
      lineWidth: 1,
      lineStyle: 2, // Dashed
      axisLabelVisible: true,
    });

    // Strong trend line (25)
    adxSeries.createPriceLine({
      price: 25,
      color: '#64748b',
      lineWidth: 1,
      lineStyle: 2, // Dashed
      axisLabelVisible: true,
    });

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
      adxSeriesRef.current = null;
    };
  }, [loading, data]);

  // Apply timezone changes without recreating chart
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      localization: {
        timeFormatter: (t: number) => formatCrosshairLabel(t, timezone),
      },
      timeScale: {
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          formatTickMarkLabel(time as number, tickMarkType, timezone),
      },
    });
  }, [timezone]);


  if (loading) {
    return (
      <div style={{ 
        height: 120, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#94a3b8',
        backgroundColor: 'rgba(30, 41, 59, 0.3)',
        borderRadius: '4px',
      }}>
        Loading ADX data...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{ 
        height: 120, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#94a3b8',
        backgroundColor: 'rgba(30, 41, 59, 0.3)',
        borderRadius: '4px',
      }}>
        No ADX data available
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ADX Panel Title */}
      <div style={{
        padding: '8px 12px',
        fontSize: '12px',
        fontWeight: 700,
        color: '#8B5CF6',
        backgroundColor: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '4px 4px 0 0',
        borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}>
        ADX (14) - Trend Strength
      </div>

      {/* Chart Container */}
      <div 
        ref={chartContainerRef}
        style={{ 
          width: '100%', 
          flex: 1,
          minHeight: 0,
          position: 'relative',
          backgroundColor: 'rgba(30, 41, 59, 0.3)',
          borderRadius: '0 0 4px 4px',
        }}
      />
    </div>
  );
};

export default AdxPanel;
